'use client';

// The voice bar (BUILD 2 C2-8, WP2-8 step 5). While voice mode is on it
// replaces the composer's input grid inside the same pill-shaped composer, in
// the operator-approved dictation vocabulary (UX round 1, U2):
//   - the live text sits in the composer's text area above the row, greyed
//     partials turning normal once final (DictationTranscript's treatment);
//     before the first word it shows what to do next, greyed. There is no
//     separate caption strip and no language control;
//   - the row holds attachments, a waveform, Type instead, Exit and a
//     44 px Send now control. Speech endpoints open the undo window;
//     no control requires holding down a pointer or key.
//   - a microphone indicator shows for as long as voice mode holds the mic;
//   - design tokens only; at 390 px the waveform shrinks before the buttons;
//     under `prefers-reduced-motion` the waveform stands still.
// When voice mode is paused (or has just ended with something to say) the bar
// is a one-line notice above the composer instead, with Resume voice and ✕;
// the composer is usable underneath.
import { useBilling } from '@/billing/context';
import { ComposerAddAttachment } from '@/components/assistant-ui/attachment';
import { Button } from '@/components/ui/button';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import voiceCopy from '@/i18n/ns/voice';
import { useChatCopy } from '@/lib/chat-i18n';
import { cn } from '@/lib/utils';
import { DictationWaveform } from '@/speech/dictation-waveform';
import { CheckIcon, KeyboardIcon, MicIcon, RotateCcwIcon, SquareIcon, XIcon } from 'lucide-react';
import { useEffect, useRef, type FC, type PointerEvent } from 'react';
import { useStore } from 'zustand';
import { useVoiceClock } from './clock';
import type { VoicePhase } from './contracts';
import type { VoiceIssue } from './issues';
import { useReducedMotion } from './reduced-motion';
import { voiceController, voiceStore } from './runtime';
import { barActive, type CaptionSegment, type PendingView, type VoiceState } from './store';

type Copy = Readonly<Record<keyof typeof voiceCopy.en, string>>;

const useVoiceState = <T,>(selector: (state: VoiceState) => T): T => useStore(voiceStore, selector);

/** Whether voice mode holds the composer, so the composer hides its input grid. */
export function useVoiceBarActive(): boolean {
  return useVoiceState((state) => barActive(state.phase));
}

export const issueText = (copy: Copy, issue: VoiceIssue): string =>
  formatCopy(copy[issue.key], issue.values ?? {});

/** Whole seconds left of a pending countdown (at least 1 while it runs). */
export const secondsLeft = (deadline: number, now: number): number =>
  Math.max(1, Math.ceil((deadline - now) / 1000));

function pendingLine(copy: Copy, pending: PendingView, now: number): string {
  if (pending.deadline !== null)
    return formatCopy(copy.sendingIn, { seconds: secondsLeft(pending.deadline, now) });
  if (pending.reason === 'degraded') return copy.degradedTapToSend;
  if (pending.reason === 'focus') return copy.answerCardFirst;
  if (pending.reason === 'paused') return copy.hearing;
  return copy.tapToSend;
}

/** voice-structural: phase to the greyed line shown before the first word. */
const PHASE_LINES: Partial<Record<VoicePhase, keyof Copy>> = {
  arming: 'starting',
  hearing: 'hearing',
  committing: 'finishing',
  sending: 'answering',
  answering: 'answering',
  speaking: 'speakingInterrupt',
  ducked: 'speakingInterrupt',
};

/**
 * What the bar says, from copy keys only.
 *   placeholder: the greyed line in the text area before the first word;
 *   label: the small line above the waveform when something needs attention.
 */
function voiceBarStatus(
  copy: Copy,
  state: Pick<VoiceState, 'phase' | 'style' | 'pending' | 'reconnecting' | 'degraded' | 'issue'>,
  now: number,
) {
  const ready = copy.listening;
  const phaseLine = PHASE_LINES[state.phase];
  const placeholder = state.pending
    ? pendingLine(copy, state.pending, now)
    : phaseLine
      ? copy[phaseLine]
      : ready;
  const label = state.reconnecting
    ? copy.reconnecting
    : state.issue
      ? issueText(copy, state.issue)
      : state.degraded
        ? copy.degraded
        : '';
  return { placeholder, label };
}

const SegmentText: FC<{ segment: CaptionSegment }> = ({ segment }) =>
  segment.final ? (
    <span data-final="true">{segment.text} </span>
  ) : (
    <span aria-hidden="true" data-final="false" className="text-muted-foreground">
      {segment.text}{' '}
    </span>
  );

/** The live text in the composer's text area: the open turn, else what to do. */
const VoiceLiveText: FC<{ copy: Copy; placeholder: string }> = ({ copy, placeholder }) => {
  const segments = useVoiceState((state) => state.segments);
  const logRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const log = logRef.current;
    if (log) log.scrollTop = log.scrollHeight;
  }, [segments]);
  return (
    <div
      ref={logRef}
      role="log"
      aria-live="polite"
      aria-relevant="additions"
      aria-label={copy.voiceMode}
      dir="auto"
      data-slot="voice-transcript"
      className="text-foreground max-h-40 min-h-11 min-w-0 overflow-y-auto px-1.5 py-2.5 text-base leading-6 break-words whitespace-pre-wrap md:min-h-10 md:px-2.5 md:py-1 md:leading-normal"
    >
      {segments.map((segment) => (
        <SegmentText key={segment.key} segment={segment} />
      ))}
      {segments.length === 0 && (
        <span aria-hidden="true" data-slot="voice-placeholder" className="text-muted-foreground">
          {placeholder}
        </span>
      )}
    </div>
  );
};

/** voice-structural: phases in which the reply is being read aloud. */
const READING_PHASES: ReadonlySet<VoicePhase> = new Set<VoicePhase>(['speaking', 'ducked']);

/** One 44 px tap starts or ends hands-free listening. */
const TalkButton: FC<{ copy: Copy; phase: VoicePhase }> = ({ copy, phase }) => {
  const controller = voiceController();
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (phase === 'arming') ref.current?.focus({ preventScroll: true });
  }, [phase]);
  const stopping = READING_PHASES.has(phase);
  const label = stopping
    ? copy.stopReply
    : phase === 'hearing'
      ? copy.finishSpeaking
      : copy.handsFreeLabel;
  return (
    <Button
      ref={ref}
      type="button"
      variant="default"
      size="icon"
      data-slot="voice-talk"
      data-stopping={stopping || undefined}
      className="size-11 shrink-0 touch-none rounded-full select-none"
      aria-label={label}
      title={label}
      onClick={() => (stopping ? controller.stopSpeaking() : controller.toggleTalk())}
    >
      {stopping ? (
        <SquareIcon className="size-4 fill-current" aria-hidden="true" />
      ) : phase === 'hearing' ? (
        <CheckIcon className="size-5" aria-hidden="true" />
      ) : (
        <MicIcon className="size-5" aria-hidden="true" />
      )}
    </Button>
  );
};

/** The one-line notice while voice mode is paused, or just ended with something to say. */
const VoiceNotice: FC<{ copy: Copy; phase: VoicePhase; issue: VoiceIssue | null }> = ({
  copy,
  phase,
  issue,
}) => {
  const chatCopy = useChatCopy();
  const controller = voiceController();
  const billingState = useBilling();
  const billing = useRef(billingState);
  billing.current = billingState;
  useEffect(() => {
    // A used-up company credit opens billing, as a chat question does (#2841).
    if (issue?.code === 'payment_required') billing.current?.request();
  }, [issue]);
  const paused = phase === 'paused';
  if (!paused && !issue) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      data-slot="voice-notice"
      data-phase={phase}
      className="border-border/70 bg-muted/50 text-foreground flex min-h-11 flex-wrap items-center gap-2 rounded-xl border px-3 py-1.5 text-sm"
    >
      <span className="min-w-0 flex-1 basis-40">
        {issue ? issueText(copy, issue) : copy.paused}
      </span>
      {paused && (
        <Button
          type="button"
          variant="default"
          data-slot="voice-resume"
          className="h-11 min-w-11 shrink-0 gap-1.5 rounded-full px-3"
          onClick={() => controller.enter()}
        >
          <RotateCcwIcon className="size-4" aria-hidden="true" />
          <span>{copy.resumeVoice}</span>
        </Button>
      )}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        data-slot="voice-dismiss"
        className="size-11 shrink-0 rounded-full"
        aria-label={chatCopy.close}
        onClick={() => controller.dismiss()}
      >
        <XIcon className="size-4" aria-hidden="true" />
      </Button>
    </div>
  );
};

export function VoiceBar() {
  const copy = useNamespaceCopy(voiceCopy);
  const phase = useVoiceState((state) => state.phase);
  const style = useVoiceState((state) => state.style);
  const pending = useVoiceState((state) => state.pending);
  const reconnecting = useVoiceState((state) => state.reconnecting);
  const degraded = useVoiceState((state) => state.degraded);
  const issue = useVoiceState((state) => state.issue);
  const level = useVoiceState((state) => state.level);
  const micClaimed = useVoiceState((state) => state.micClaimed);
  const still = useReducedMotion();
  const now = useVoiceClock(pending?.deadline != null);
  if (!barActive(phase))
    return (
      <>
        <VoiceNotice copy={copy} phase={phase} issue={issue} />
      </>
    );
  const controller = voiceController();
  const { placeholder, label } = voiceBarStatus(
    copy,
    { phase, style, pending, reconnecting, degraded, issue },
    now,
  );
  const hearing = phase === 'hearing';
  const cancelLabel = hearing ? copy.cancelSpeaking : pending ? copy.discard : copy.exitVoice;
  return (
    <div
      data-slot="voice-bar"
      data-phase={phase}
      data-style={style}
      role="group"
      aria-label={copy.voiceMode}
      className="flex w-full min-w-0 flex-col gap-1"
      onClick={(event) => {
        // Buttons keep their own actions; the rest of the voice surface is a
        // stop target while output is playing, including the text and waveform.
        if ((event.target as Element).closest('button, a, input, textarea, select')) return;
        if (READING_PHASES.has(phase)) controller.stopSpeaking();
      }}
      onKeyDown={(event) => {
        if (event.key !== 'Escape' || event.nativeEvent.isComposing) return;
        event.preventDefault();
        event.stopPropagation();
        controller.escape();
      }}
    >
      <VoiceLiveText copy={copy} placeholder={placeholder} />
      <div data-slot="voice-row" className="flex min-w-0 items-center gap-1 md:gap-1.5">
        <div inert className="flex shrink-0 items-center opacity-40">
          <ComposerAddAttachment />
        </div>
        <div className="flex min-w-0 flex-1 flex-col justify-center px-1">
          <span
            data-slot="voice-status"
            className="text-muted-foreground flex min-w-0 items-center gap-1.5 truncate text-xs leading-4"
          >
            {micClaimed && (
              <span data-slot="voice-mic-indicator" className="flex shrink-0 items-center gap-1">
                <span aria-hidden="true" className="bg-destructive size-2 rounded-full" />
                <span className="sr-only">{copy.micOn}</span>
              </span>
            )}
            <span className={cn('truncate', label && 'text-foreground font-medium')}>{label}</span>
          </span>
          <DictationWaveform
            level={level}
            phase={!still && (hearing || phase === 'ready') ? 'live' : 'idle'}
            className="h-6"
          />
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          data-slot="voice-type"
          className="text-muted-foreground hover:text-foreground size-11 shrink-0 rounded-full"
          aria-label={copy.typeInstead}
          title={copy.typeInstead}
          onClick={() => controller.exit()}
        >
          <KeyboardIcon className="size-5" aria-hidden="true" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          data-slot="voice-cancel"
          className="text-muted-foreground hover:text-foreground size-11 shrink-0 rounded-full"
          aria-label={cancelLabel}
          title={cancelLabel}
          onClick={() => {
            if (hearing) controller.cancelTalk();
            else if (pending) controller.discardPending();
            else controller.exit();
          }}
        >
          <XIcon className="size-5" aria-hidden="true" />
        </Button>
        <TalkButton copy={copy} phase={phase} />
      </div>
    </div>
  );
}
